import * as React from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

const { useEffect, useRef, useState } = React;

/**
 * Tooltip — small floating annotation surface, one valid implementation
 * of design/components/tooltip.md.
 *
 * Vendored from shadcn (new-york / radix-ui umbrella style). Replace
 * with `pnpm dlx shadcn@latest add tooltip` to pick up upstream
 * changes; the primitive imports use the umbrella `radix-ui` package
 * (consistent with `alert-dialog.tsx`).
 *
 * Composition:
 *
 *   TooltipProvider (one at the route root via `main.tsx`)
 *     └─ Tooltip                          // open/close state
 *         ├─ TooltipTrigger               // the hoverable / focusable element
 *         └─ TooltipContent               // the floating bubble (portal-rendered)
 *
 * Accessibility:
 *  - Radix supplies hover-and-focus reveal, Escape-to-dismiss, and the
 *    `aria-describedby` wiring from the trigger to the content.
 *  - Pointer-events on `TooltipContent` come through a Portal at the
 *    document root, so the content's hit-testing is independent of the
 *    trigger's parent's `pointer-events` — this is what unlocks the
 *    `FeedbackOverlay` span case where the highlight is itself
 *    `pointer-events: none` (the trigger's hover detection survives
 *    even though the span doesn't capture pointer events directly).
 *  - The Tooltip surface is decorative — screen readers see the
 *    content through `aria-describedby` rather than as an interactive
 *    region. Disabled triggers still announce their tooltip via the
 *    `aria-describedby` wiring as long as the trigger remains focusable.
 *
 * Provider placement:
 *  One `<TooltipProvider>` wraps the app (currently in `main.tsx`
 *  around the `RouterProvider`, so every route descendant shares the
 *  same `skipDelayDuration` — once you've hovered one tooltip, the next
 *  ones reveal faster). Storybook gets the same wiring via a decorator
 *  in `.storybook/preview.tsx` so stories that consume Tooltip render
 *  the same way the app does.
 *
 * Provider knob:
 *  `delayDuration` defaults to `0` here (instant reveal) — the live
 *  consumers all want fast feedback. Individual `Tooltip` instances can
 *  override per-component when a slower reveal reads better (e.g. the
 *  `?` help icon could benefit from a 200ms delay to avoid flashing
 *  during cursor pass-throughs).
 */

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          // Canonical tooltip treatment — the single source of truth for
          // every tooltip's sizing + spacing, so call sites never re-decide
          // it (they used to each pass their own `max-w-*`, which drifted).
          //   - `max-w-xs` caps the bubble at a comfortable reading column;
          //     `w-fit` lets short copy stay narrow.
          //   - `text-left` + plain wrapping (NO `text-wrap: balance`/`pretty`):
          //     left-justified prose that fills each line greedily left-to-right
          //     — even, and rendered identically on every browser. The CSS
          //     `text-wrap` levers (`balance`/`pretty`) are recent and NOT
          //     honoured on mobile Safari, so using them for layout fixes
          //     desktop but leaves phones ragged (the desktop-only-fix bug this
          //     replaces); `balance` also visibly "spreads" short copy, which
          //     was the original report.
          //   - `px-3 py-1.5` + `leading-snug`: comfortable, consistent inset.
          'z-50 w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin) animate-in rounded-md bg-foreground px-3 py-1.5 text-left text-xs/snug text-background fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

/**
 * ClickStateTooltip — compositional wrapper that opens on click and stays
 * open until the user clicks outside (or presses Escape, or the trigger
 * loses focus). The "click to stay open, click out to close" UX Ryan asked
 * for after Path A (force-focus on touch) didn't survive Radix's
 * close-on-trigger-activate behavior.
 *
 * Why this is its own helper instead of a flag on `Tooltip`:
 *
 *   - Radix Tooltip's `close on activate` is fundamental to the
 *     uncontrolled Tooltip. Overriding it cleanly requires controlled
 *     `open` state, which the consumer would otherwise have to wire by
 *     hand at every call site. The helper packages the state machine and
 *     the outside-pointerdown listener once.
 *   - The hover-only Tooltip remains useful for purely decorative cases
 *     where touch reveal isn't needed (the snippet preview in
 *     `FeedbackGutter` — the un-truncated form is a nice-to-have, the
 *     message body next to it carries the load-bearing copy). Keeping
 *     both primitives lets each call site choose explicitly.
 *
 * Wire shape mirrors `<Tooltip><TooltipTrigger asChild>{child}</…><TooltipContent>{content}</…></Tooltip>`
 * but flattened to one props blob:
 *
 *   <ClickStateTooltip content={message}>
 *     <button>Trigger</button>
 *   </ClickStateTooltip>
 *
 * Hover still works on desktop — Radix's hover handlers call
 * `onOpenChange(true)` and our controlled state honors them. The click
 * handler sets `open=true` and overrides Radix's pointerdown-close
 * (which fires before click in React's synthetic event ordering, then
 * gets superseded by our `setOpen(true)`). Outside-pointerdown closes
 * via a document-level listener that excludes both the trigger and the
 * portaled content. Tap on mobile lands through the same `click` event
 * (synthesized from touch), so no special touch handling is required.
 *
 * The trigger child needs to be a single ReactElement (one DOM element).
 * Radix's `asChild` Slot composes refs and event handlers; consumers'
 * existing `onClick` on the child runs alongside our `setOpen(true)`.
 */
interface ClickStateTooltipProps
  extends Omit<
    React.ComponentProps<typeof TooltipPrimitive.Root>,
    'children' | 'open' | 'onOpenChange' | 'defaultOpen'
  > {
  children: React.ReactElement;
  content: React.ReactNode;
  /** Optional className for the floating `TooltipContent`. */
  contentClassName?: string;
}

function ClickStateTooltip({
  children,
  content,
  contentClassName,
  ...rootProps
}: ClickStateTooltipProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLElement>(null);

  // Outside-pointerdown closes. Mount the listener only when open so
  // every tooltip on the page isn't fielding global events all the
  // time. `capture: true` so we catch the event before any nested
  // stopPropagation upstream.
  useEffect(() => {
    if (!open) return;
    const handler = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // Inside the trigger → leave alone (the trigger's own onClick
      // handles toggle / re-open; this listener should not close it
      // before the click bubbles).
      if (triggerRef.current?.contains(target)) return;
      // Inside the portaled tooltip content → leave alone (clicks
      // inside the bubble shouldn't dismiss it).
      if (target.closest('[data-slot="tooltip-content"]')) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', handler, true);
    return () => {
      document.removeEventListener('pointerdown', handler, true);
    };
  }, [open]);

  return (
    <TooltipPrimitive.Root
      data-slot="tooltip"
      open={open}
      onOpenChange={setOpen}
      {...rootProps}
    >
      <TooltipPrimitive.Trigger
        ref={triggerRef as React.Ref<HTMLButtonElement>}
        data-slot="tooltip-trigger"
        asChild
        // React fires `onClick` AFTER Radix's internal `onPointerDown`
        // (which calls `onOpenChange(false)` on activate). The result:
        // pointerdown sets state false → click sets state true →
        // tooltip ends up open. The order is reliable in React's
        // synthetic event system.
        onClick={() => {
          setOpen(true);
        }}
      >
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipContent className={contentClassName}>{content}</TooltipContent>
    </TooltipPrimitive.Root>
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  ClickStateTooltip,
};
